import React from 'react';
import { Cloud, CloudOff, Loader, HardDrive, AlertCircle } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

/**
 * Sync status indicator component
 * Shows the current sync state of the collection
 *
 * @param {string} status - 'synced' | 'syncing' | 'offline' | 'local-only' | 'error'
 * @param {string} size - 'sm' | 'md' (default: 'md')
 * @param {boolean} showLabel - Whether to show the status text (default: true)
 */
export default function SyncStatusIndicator({ status = 'local-only', size = 'md', showLabel = true }) {
  const { t } = useI18n();

  const iconSize = size === 'sm' ? 14 : 16;

  const statusConfig = {
    synced: {
      icon: <Cloud size={iconSize} className="text-green-500" />,
      label: t('syncStatus.synced'),
      textClass: 'text-green-600',
      bgClass: 'bg-green-50'
    },
    syncing: {
      icon: <Loader size={iconSize} className="text-blue-500 animate-spin" />,
      label: t('syncStatus.syncing'),
      textClass: 'text-blue-600',
      bgClass: 'bg-blue-50'
    },
    offline: {
      icon: <CloudOff size={iconSize} className="text-amber-500" />,
      label: t('syncStatus.offline'),
      textClass: 'text-amber-600',
      bgClass: 'bg-amber-50'
    },
    'local-only': {
      icon: <HardDrive size={iconSize} className="text-slate-400" />,
      label: t('syncStatus.localOnly'),
      textClass: 'text-slate-500',
      bgClass: 'bg-slate-50'
    },
    error: {
      icon: <AlertCircle size={iconSize} className="text-red-500" />,
      label: t('syncStatus.error'),
      textClass: 'text-red-600',
      bgClass: 'bg-red-50'
    }
  };

  const config = statusConfig[status] || statusConfig['local-only'];

  return (
    <div className={`inline-flex items-center gap-1.5 ${showLabel ? `px-2 py-1 rounded-full ${config.bgClass}` : ''}`}>
      {config.icon}
      {showLabel && (
        <span className={`text-xs font-medium ${config.textClass}`}>
          {config.label}
        </span>
      )}
    </div>
  );
}
